import React, { useEffect, useRef, useState } from 'react';

import { ChevronDoubleRightIcon, FilterIcon } from '@vlabs/icons';
import {
  Page, PageContent, PageHeader, usePageContext,
} from '@vlabs/monokit/shared/ui/page';
import { FiltersHeader } from '@vlabs/shared/components/filters-panel/FiltersHeader';
import { useDebouncedSetFilters } from '@vlabs/shared/components/filters-panel/hooks';
import { routes } from '@vlabs/shared/navigation/routes';
import {
  Table, Panel, Control, PageWithSidebar, Badge,
} from '@vlabs/uikit';
import cn from 'classnames';
import { throttle } from 'lodash';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import {
  selectLatestEvents, selectLatestEventsFilters, selectSound, selectFilterCounts, selectIsTopMatchHiding,
} from '@vlabs/pages/events/latest-events/selectors';
import { updateFilters, setParams, setIsTopMatchHiding } from '@vlabs/pages/events/latest-events/store';

import FilterForm from '../components/FilterFormLatestEvents';
import { useEventTableColumns } from '../eventTableColumns';
import { FilterSoundNotification } from './sound-notification/FilterSoundNotification';
import './LatestEventListPage.sass';

function LatestEventListPage({
  filters = {},
  filterCounts = 0,
  setFilters = undefined,
  eventsData = {},
  sound = {},
  setIsSoundOn = undefined,
  setSoundThreshold = undefined,
  isTopMatchHiding = false,
  toggleTopMatchVisibility = undefined,
  includedAttributes = [],
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { getTitleComponent } = usePageContext();
  const [isSidebarOpened, setIsSidebarOpened] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const formMethods = useForm({ defaultValues: filters });
  const lastScrollTop = useRef(0);

  const { watch, trigger } = formMethods;
  useDebouncedSetFilters({ trigger, watch }, setFilters);

  useEffect(() => {
    const tableBody = document.querySelector('.TableBody');
    if (!tableBody) return;

    const handleScroll = throttle(() => {
      const { scrollTop, scrollHeight, clientHeight } = tableBody;

      // если мы в самом низу таблицы — просто выходим
      if (scrollTop + clientHeight >= scrollHeight - 1) return;

      if (Math.abs(scrollTop - lastScrollTop.current) < 10) return;

      setIsHeaderVisible(scrollTop <= lastScrollTop.current);
      lastScrollTop.current = scrollTop;
    }, 100);

    tableBody.addEventListener('scroll', handleScroll);
    return () => tableBody.removeEventListener('scroll', handleScroll);
  }, []);

  const onEventCardClick = ({ event_id: eventId }) => {
    const eventLink = generatePath(routes.events.eventDetails, { eventId });
    history.push(eventLink);
  };

  const onTopMatchCardClick = ({ face_id, event_id }) => {
    if (face_id) {
      history.push(generatePath(routes.facePage, { faceId: face_id }));
    }
    if (event_id) {
      history.push(generatePath(routes.events.eventDetails, { eventId: event_id }));
    }
  };

  const columns = useEventTableColumns(onTopMatchCardClick, onEventCardClick, isTopMatchHiding, includedAttributes);

  const context = {
    ...formMethods,
    setFilters,
    sound,
    setIsSoundOn,
    setSoundThreshold,
  };

  const topMatchAndNotificationSwitches = (
    <div className="LatestEventListPage__HeaderSwitches">
      <Control.Switch
        className="LatestEventListPage__TableTopPanel"
        label={t('events:display_top_match')}
        onChange={toggleTopMatchVisibility}
        value={!isTopMatchHiding}
      />
      <div className="LatestEventListPage__DividerSmall" />
      <FilterSoundNotification />
    </div>
  );

  return (
    <FormProvider {...context}>
      <form style={{ flex: 1 }}>
        <PageWithSidebar
          className="FullHeightSidebar"
          isOpenRightSidebar={isSidebarOpened}
          rightSidebar={(
            <React.Fragment>
              <FiltersHeader
                isAdaptive
                onClick={() => setIsSidebarOpened((prev) => !prev)}
              />
              <FilterForm />
            </React.Fragment>
          )}
        >
          <Page className={cn('LatestEventListPage', 'LatestEventListPage__Page')}>
            <PageContent>
              <PageHeader
                actions={(
                  <Control.Button
                    className="LatestEventListPage__FiltersButton"
                    data-testid="filtersSidebar.toggleButton"
                    icon={isSidebarOpened ? <ChevronDoubleRightIcon /> : <FilterIcon />}
                    kind={isSidebarOpened ? 'secondary' : 'primary'}
                    onClick={() => setIsSidebarOpened((prev) => !prev)}
                    variant={isSidebarOpened ? 'filled' : 'outlined'}
                  >
                    {t('filters_title')}
                    {filterCounts > 0 && <Badge value={filterCounts} />}
                  </Control.Button>
                  )}
                className={cn({ LatestEventListPage__Header_hidden: !isHeaderVisible })}
                title={(
                  <React.Fragment>
                    {getTitleComponent(t('events:page.latest_events.title'))}
                    {topMatchAndNotificationSwitches}
                  </React.Fragment>
                  )}
              />

              <div className="LatestEventListPage__TableWrapper">
                <Panel className="LatestEventListPage__Table_custom PanelAutoHeight">
                  <Table
                    columns={columns}
                    data={eventsData.data}
                    pageIndex={eventsData.pageIndex}
                    pageSize={eventsData.pageSize}
                    rowProps={(row) => ({ 'data-event-id': row.original?.event_id })}
                  />
                </Panel>
              </div>

              <div className="LatestEventListPage__ArchiveButton">
                <Control.Button
                  data-testid="eventsPage.goToArchiveButton"
                  onClick={() => history.push(generatePath(routes.events.archive))}
                  variant="outlined"
                >
                  {t('events:view_archive')}
                </Control.Button>
              </div>
            </PageContent>
          </Page>
        </PageWithSidebar>
      </form>
    </FormProvider>
  );
}

LatestEventListPage.propTypes = {
  eventsData: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.any)]),
    pageSize: PropTypes.number,
    pageIndex: PropTypes.number,
    pageSizeOptions: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  filters: PropTypes.objectOf(PropTypes.any),
  filterCounts: PropTypes.number,
  setFilters: PropTypes.func,
  sound: PropTypes.objectOf(PropTypes.any),
  setIsSoundOn: PropTypes.func,
  setSoundThreshold: PropTypes.func,
  isTopMatchHiding: PropTypes.bool,
  toggleTopMatchVisibility: PropTypes.func,
  includedAttributes: PropTypes.arrayOf(PropTypes.string),
};

export default connect(
  (state) => ({
    eventsData: selectLatestEvents(state),
    filters: selectLatestEventsFilters(state),
    filterCounts: selectFilterCounts(state),
    sound: selectSound(state),
    isTopMatchHiding: selectIsTopMatchHiding(state),
  }),
  (dispatch) => ({
    setIsSoundOn: (isSoundOn) => dispatch(setParams({ isSoundOn })),
    setSoundThreshold: (soundThreshold) => dispatch(setParams({ soundThreshold })),
    setFilters: (filters) => dispatch(updateFilters({ filters })),
    toggleTopMatchVisibility: () => dispatch(setIsTopMatchHiding()),
  }),
)(LatestEventListPage);
